import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  List,
  Container,
  Box,
  ListItemButton,
} from "@mui/material";
import { listProducts } from "../redux/slices/productSlice";
import ProductDetailsModal from "../components/ProductDetailsModal";
import { ProductDetail } from "../interfaces/product-interface";
import useTabTitle from "../hooks/useTabTitle.hook";
import useDetectSession from "../hooks/useDetectSession.hook";
import AppContainer from "../components/AppContainer";
import { pageHeading } from "../styles/styling";
import LoadingOverlay from "../components/LoadingOverlay";

const ProductsPage: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetail | null>(
    null
  );

  const { products, status, error } = useSelector(
    (state: RootState) => state.products
  );

  const idToken = localStorage.getItem("idToken");
  useDetectSession();

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }

    dispatch(listProducts());
  }, [dispatch, idToken, navigate]);

  const handleProductClick = (productName: string) => {
    navigate(`/view-product`, {
      state: { productName },
    });
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  if (error) {
    return (
      <AppContainer>
        <Container sx={{ marginLeft: "240px", flexGrow: 1, padding: 2 }}>
          <Typography variant="h6" color="error">
            Error: {error}
          </Typography>
        </Container>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Typography variant="h4" sx={pageHeading}>
            Products List
          </Typography>
        </Box>

        <LoadingOverlay loading={status === "loading"} />

        <ListItemButton
          component={Box}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={5}
          sx={{ paddingX: 1, backgroundColor: "#f5f5f5" }}
          disabled
        >
          <Box display="flex" justifyContent="space-between" width="80%">
            <Typography variant="body1" sx={{ flex: 1, fontWeight: "bold" }}>
              Name
            </Typography>
            <Typography
              variant="body1"
              sx={{ flex: 1, textAlign: "right", fontWeight: "bold" }}
            >
              Category
            </Typography>
          </Box>
        </ListItemButton>

        {products.length === 0 ? (
          <Typography>No products found</Typography>
        ) : (
          <List>
            {products.map((product) => (
              <ListItemButton
                key={product?.name}
                component={Box}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                onClick={() => handleProductClick(product?.name as string)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="80%"
                  sx={{ paddingX: 2 }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, textAlign: "right" }}
                  >
                    {product.category}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
        {selectedProduct && (
          <ProductDetailsModal
            open={modalOpen}
            onClose={handleClose}
            product={selectedProduct}
          />
        )}
      </Box>
    </AppContainer>
  );
};

export default ProductsPage;
